'use client';

import React, { useState } from 'react';
import OrderStatusTabs from './OrderStatusTabs';
import OrderFilters from './OrderFilters';
import OrderTable from './OrderTable';
import OrderDetailPanel from './OrderDetailPanel';
import { ShoppingCart } from 'lucide-react';

export type OrderStatus = 'all' | 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'return' | 'dispute';

export interface Order {
  id: string;
  customer: string;
  customerCity: string;
  customerPhone: string;
  customerEmail: string;
  productCount: number;
  date: string;
  amount: number;
  payment: 'COD' | 'Online' | 'Wallet';
  status: Exclude<OrderStatus, 'all'>;
  fba: boolean;
  trackingNumber: string | null;
  shippingAddress: string;
  items: { name: string; sku: string; qty: number; price: number; image: string; imageAlt: string }[];
  timeline: { event: string; time: string; done: boolean }[];
}

export default function OrderManagementContent() {
  const [activeStatus, setActiveStatus] = useState<OrderStatus>('all');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [filters, setFilters] = useState({ search: '', dateFrom: '', dateTo: '', minAmount: '', maxAmount: '' });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <ShoppingCart size={22} className="text-primary" />
            Order Management
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Ahmed Electronics — Process and track all customer orders
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-xs">Export Orders</button>
          <button className="btn-primary text-xs">Bulk Ship Selected</button>
        </div>
      </div>

      {/* Status tabs */}
      <OrderStatusTabs active={activeStatus} onChange={setActiveStatus} />

      {/* Filters */}
      <OrderFilters filters={filters} onChange={setFilters} />

      {/* Table + Detail panel */}
      <div className={`flex gap-5 transition-all duration-300 ${selectedOrder ? 'items-start' : ''}`}>
        <div className={`flex-1 min-w-0 transition-all duration-300 ${selectedOrder ? 'xl:w-[55%]' : 'w-full'}`}>
          <OrderTable
            activeStatus={activeStatus}
            filters={filters}
            selectedIds={selectedIds}
            onSelectIds={setSelectedIds}
            onSelectOrder={setSelectedOrder}
            selectedOrderId={selectedOrder?.id ?? null}
          />
        </div>

        {selectedOrder && (
          <div className="xl:w-[45%] w-full shrink-0">
            <OrderDetailPanel
              order={selectedOrder}
              onClose={() => setSelectedOrder(null)}
            />
          </div>
        )}
      </div>
    </div>
  );
}